import i18next, { PostProcessorModule } from 'i18next';
import HttpBackend, { type HttpBackendOptions } from 'i18next-http-backend';
import { useAccountStore } from '@/stores/accountStore';
import { isNewKey, mergeTranslations } from './utils';
import { useTranslationKeyStore } from '@/stores/translationKeyStore';
import { useSystemStore } from '@/stores/systemStore';
import { OVERWRITE_USE_LIVE_TRANSLATIONS } from '@/env';

export const defaultNS = 'translation';

const debugKeysPostProcessor: PostProcessorModule = {
  type: 'postProcessor',
  name: 'debugKeys',
  process(value, key) {
    if (useTranslationKeyStore().showTranslationKeys) {
      const keyPath = Array.isArray(key) ? key.join('.') : key;
      return isNewKey(keyPath) ? `[[${keyPath}]]` : `[${keyPath}]`;
    }
    return value;
  },
};

export async function loadI18Next() {
  const systemStore = useSystemStore();
  const supportedLngs = systemStore.languages?.map((language) => language.name);

  const useLiveTranslation = systemStore.useLiveTranslations || OVERWRITE_USE_LIVE_TRANSLATIONS;

  await i18next
    .use(debugKeysPostProcessor)
    .use(HttpBackend)
    .init<HttpBackendOptions>({
      postProcess: ['debugKeys'],
      lng: useAccountStore().language,
      load: 'languageOnly',
      debug: false,
      fallbackLng: 'en',
      defaultNS,
      supportedLngs,
      interpolation: {
        escapeValue: false, // Vue will already properly escape values intended as plain text
        skipOnVariables: false,
      },
      backend: {
        loadPath: '/api/localization/languages/{{lng}}/translations',
        parse: (data) =>
          useLiveTranslation ? mergeTranslations(JSON.parse(data)) : JSON.parse(data),
        queryStringParams: useLiveTranslation ? { live: 'true' } : undefined,
      },
      partialBundledLanguages: true,
    });

  i18next.services.formatter?.add('lowercase', (value) => {
    return value[0].toLowerCase() + value.slice(1);
  });

  return i18next;
}
